/**
 * The public pages' previews (the user, 2026-10-14: "all samples from the real
 * view of the app"): the rave app ITSELF, framed by the master welcome or a
 * rave's landing page at `<rave>.<domain>/<edition>?preview=<screen>`.
 *
 * Preview mode is only ever FRAMED (`isFramed`) — the same address opened in
 * a tab is the ordinary app. In it the app is signed out on purpose and asks
 * nobody for a session (`lib/api.ts` `exchange`), reads its data from the open
 * `GET /public/rave/bundle` (one sample day and the live window, the bundle's
 * own shape — `offline/bundle.ts` `fetchPreviewBundle`), stores nothing, sends
 * no statistics (framed), and is drawn read-only (`ReadOnly`, `App.tsx`
 * `RaveHost`): never the sign-in redirect, never the rave's welcome page.
 */
import { LANG_PARAM } from '@myrave/shared';
import { isFramed } from './framed';

export const PREVIEW_SCREENS = ['live', 'schedule', 'grid', 'lineup', 'map', 'artist', 'countdown'] as const;
export type PreviewScreen = (typeof PREVIEW_SCREENS)[number];

export const PREVIEW_PARAM = 'preview';

const isScreen = (v: unknown): v is PreviewScreen => PREVIEW_SCREENS.includes(v as PreviewScreen);

/** The screen this framed page previews, or null — the app as usual. */
export function previewScreen(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
  framed: boolean = isFramed(),
): PreviewScreen | null {
  if (!framed) return null;
  const v = new URLSearchParams(search).get(PREVIEW_PARAM);
  return isScreen(v) ? v : null;
}

export function inPreview(): boolean {
  return previewScreen() !== null;
}

/**
 * Open the app on the previewed screen: its hash route (`FestivalApp.tsx`),
 * set once before the app reads it. A hash already there wins.
 */
export function enterPreviewScreen(): void {
  const screen = previewScreen();
  if (!screen) return;
  // The chrome around the screen — header, menu, tab bar, banners — is not drawn (`ui/components.css`).
  document.documentElement.setAttribute('data-preview', screen);
  // On the master page a preview names no rave in its chrome: MyRave's brand in the header (`?brand=platform`).
  if (new URLSearchParams(window.location.search).get('brand') === 'platform') document.documentElement.setAttribute('data-preview-anon', '');
  /*
   * A picture never scrolls — and must never scroll the PAGE around it. The
   * app's own jumps (an act opened by `#act=`, a lit card, a focus) call
   * `scrollIntoView`, which also scrolls the framing page: the artist
   * preview yanked the welcome page down to itself, and frames still waiting
   * to load were left behind as bare posters.
   */
  Element.prototype.scrollIntoView = function scrollIntoViewInPreview() { /* a picture does not move */ };
  window.scrollTo = () => {};
  window.scrollBy = () => {};
  const focus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function focusInPreview(this: HTMLElement, options?: FocusOptions) {
    focus.call(this, { ...options, preventScroll: true });
  };
  if (window.location.hash) return;
  // `artist` becomes `#act=<slug>` once the bundle names one (`applyPreview`).
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${screen}`);
}

/*
 * "My saved", previewed: a handful of REAL sets from the preview bundle,
 * picked in memory (the user, 2026-10-14) — never written to storage, never
 * sent. `useFavorites` reads them instead of the device's list.
 */
const picks = new Map<string, readonly string[]>();

export function previewPicks(editionId: string): readonly string[] | null {
  return inPreview() ? picks.get(editionId) ?? [] : null;
}

interface PickableAct { slug: string; startAt: string; endAt: string; trackId?: string | null; photoUrl?: string | null; blurb?: string | null; audioUrl?: string | null; links?: Record<string, string> | null; id: string }

/** Up to six sets of the sample day, a real clash among them when there is one. */
export function choosePicks(acts: readonly PickableAct[], max = 6, now = Date.now()): string[] {
  // While the rave is on, finished sets leave My lineup — so pick from those still to come.
  const ahead = acts.filter((a) => Date.parse(a.endAt) > now);
  const pool = ahead.length >= 2 ? ahead : acts;
  const t = (iso: string) => Date.parse(iso);
  const sorted = [...pool].sort((a, b) => t(a.startAt) - t(b.startAt));
  const out: string[] = [];
  for (let i = 0; i < sorted.length && out.length === 0; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      if (t(sorted[j]!.startAt) >= t(sorted[i]!.endAt)) break;
      out.push(sorted[i]!.slug, sorted[j]!.slug);
      break;
    }
  }
  const step = Math.max(1, Math.floor(sorted.length / max));
  for (let i = 0; i < sorted.length && out.length < max; i += step) {
    if (!out.includes(sorted[i]!.slug)) out.push(sorted[i]!.slug);
  }
  return out;
}

/** The act to preview as "artist": the bundle's choice, else the richest one here. */
export function chooseArtist(acts: readonly PickableAct[], featuredId?: string | null, wanted?: string | null): string | null {
  if (wanted && acts.some((a) => a.slug === wanted)) return wanted;
  const featured = featuredId ? acts.find((a) => a.id === featuredId) : undefined;
  if (featured) return featured.slug;
  const score = (a: PickableAct) => (a.photoUrl ? 4 : 0) + (a.blurb ? Math.min(3, a.blurb.length / 200) : 0)
    + (a.audioUrl ? 2 : 0) + Object.keys(a.links ?? {}).length;
  const best = [...acts].sort((a, b) => score(b) - score(a))[0];
  return best?.slug ?? null;
}

/**
 * Once the preview's bundle is here: hold its picks, and open the artist
 * screen on the chosen act (`#act=<slug>`). Called before the app renders.
 */
export function applyPreview(editionId: string, acts: readonly PickableAct[], featuredId?: string | null): void {
  if (!inPreview()) return;
  if (!picks.has(editionId)) picks.set(editionId, choosePicks(acts));
  if (previewScreen() === 'artist' && window.location.hash === '#artist') {
    const slug = chooseArtist(acts, featuredId, new URLSearchParams(window.location.search).get('artist'));
    if (slug) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#act=${encodeURIComponent(slug)}`);
  }  announceReady();
}

/** What a preview tells the page framing it once its screen is drawn (`Showcase`'s `Preview`). */
export const PREVIEW_READY = 'myrave:preview-ready';

/**
 * Tell the framing page the screen is drawn — two frames after the bundle
 * arrived, by which time React has painted it. The page keeps the frame
 * hidden until then, so a refresh shows the rave's cover and then the screen,
 * never the app's own loading screen (the user, 2026-10-14: "the previews
 * always show loading … remove this"). The message carries nothing but its
 * type, so any parent may hear it.
 */
export function previewDrawn(): void {
  announceReady();
}

/**
 * Is the screen drawn? Something on the page, and no loading screen or
 * skeleton (`data-boot-wait`) left. The bundle arriving is not enough: on a
 * slow machine the screen's own chunk was still loading, the word went out,
 * and the public page revealed a loading screen (real dev, 2026-10-15).
 */
function screenDrawn(): boolean {
  if (document.querySelector('[data-boot-wait]')) return false;
  const root = document.getElementById('root') ?? document.body;
  return (root.textContent ?? '').trim().length > 0;
}

/** Give up waiting after this long, and say it anyway — the page reveals by then regardless. */
const DRAWN_PATIENCE_MS = 20_000;
let announcing = false;

function announceReady(): void {
  if (window.parent === window || announcing) return;
  announcing = true;
  const post = () => { announcing = false; window.parent.postMessage({ type: PREVIEW_READY }, '*'); };
  const started = Date.now();
  const check = () => {
    if (!screenDrawn() && Date.now() - started < DRAWN_PATIENCE_MS) { setTimeout(check, 100); return; }
    // Two frames after it is in the DOM, by which time it is painted.
    if (typeof requestAnimationFrame !== 'function') { post(); return; }
    requestAnimationFrame(() => requestAnimationFrame(post));
  };
  check();
}

/** A rave whose screen a preview shows: its address and a name for the frame's title. */
export interface PreviewSource {
  url: string;
  name: string;
  /** For the artist screen: the act to open (`?artist=`); null = the bundle's featured act. */
  artist?: string | null;
  /** The master page's previews name no rave: MyRave's brand in the frame's header, general titles. */
  anon?: boolean;
}

export interface PreviewSources {
  live: PreviewSource | null;
  schedule: PreviewSource | null;
  grid: PreviewSource | null;
  lineup: PreviewSource | null;
  map: PreviewSource | null;
  artist: PreviewSource | null;
}

/** No rave to frame for any screen — the master page, whose screens are pictures (`Showcase.tsx` `ShotCard`). */
export const NO_SOURCES: PreviewSources = { live: null, schedule: null, grid: null, lineup: null, map: null, artist: null };

/** What a page knows of its own rave — `RaveSample`'s shape, the parts the rule reads. */
export interface OwnRave {
  rave: { url: string; festivalName: string; coverUrl: string | null; live: boolean };
  counts: { acts: number };
  lineup: readonly { photoUrl: string | null }[];
  map: unknown | null;
}

/** Where the previews fall back to — `PublicShowcase`. */
export interface Showcase {
  sample: { url: string; editionName: string } | null;
  map: { url: string; editionName: string } | null;
  artist: string | null;
}

/**
 * Which rave each preview shows (the user, 2026-10-15: "first use the real
 * rave's data, then fall back to the main"). ONE rule for every page, from
 * data alone — no rave is named in code:
 * - the timetable, the grid and My lineup: the page's own rave when it has
 *   sets, else the showcase's sample rave;
 * - the map: the own rave when it has a map, else the showcase's map rave
 *   (on the master page the showcase map comes first — it is the clearest);
 * - the artist: the own rave's artist who has something to show — a photo
 *   and a story or links (`featuredArtist`, from the API; the user,
 *   2026-10-15: "use an artist that has data") — else the sample rave with
 *   the showcase's artist, never an empty artist sheet;
 * - live: the own rave, only while it is on.
 * The master page passes its sample rave as `own`.
 */
export function previewSources(
  own: OwnRave | null, showcase: Showcase | null | undefined, page: 'master' | 'rave',
  /** The rave page's own data-rich artist (`PublicRave.featuredArtist`); null = it has none. */
  featuredArtist?: string | null,
): PreviewSources {
  const sc = showcase ?? { sample: null, map: null, artist: null };
  const mine = (o: OwnRave): PreviewSource => ({ url: o.rave.url, name: o.rave.festivalName });
  const fallback = sc.sample ? { url: sc.sample.url, name: sc.sample.editionName } : null;
  const hasSets = Boolean(own && own.counts.acts > 0 && own.lineup.length > 0);
  const sets = own && hasSets ? mine(own) : fallback;
  const showcaseMap = sc.map ? { url: sc.map.url, name: sc.map.editionName } : null;
  const ownMap = own && own.map ? mine(own) : null;
  const map = page === 'master' ? showcaseMap ?? ownMap : ownMap ?? showcaseMap;
  // The master page's own rave IS the sample, with the showcase's artist. A rave
  // page shows its own artist when one has data; else it borrows the sample's.
  const ownArtist = !own || !hasSets ? null
    : page === 'master' ? { ...mine(own), artist: sc.artist }
    : featuredArtist ? { ...mine(own), artist: featuredArtist }
    : featuredArtist === undefined ? { ...mine(own), artist: null }
    : null;
  const artist = ownArtist ?? (fallback ? { ...fallback, artist: sc.artist } : null);
  const out: PreviewSources = {
    live: own && own.rave.live ? mine(own) : null,
    schedule: sets,
    grid: sets,
    lineup: sets,
    map,
    artist,
  };
  if (page === 'master') {
    for (const k of Object.keys(out) as (keyof PreviewSources)[]) if (out[k]) out[k] = { ...out[k]!, anon: true };
  }
  return out;
}

/** The address of one screen of a rave, as a preview (`card.url` is the rave's own address). */
export function previewUrl(raveUrl: string, screen: PreviewScreen, artist?: string | null, anon?: boolean, lang?: string | null): string {
  try {
    const u = new URL(raveUrl);
    u.searchParams.set(PREVIEW_PARAM, screen);
    if (artist) u.searchParams.set('artist', artist);
    if (anon) u.searchParams.set('brand', 'platform');
    // The frame speaks the PAGE's language (2026-10-30): a rave host is not a landing page, so on
    // its own it would pick the device's (or English) while the page around it took the location's.
    if (lang) u.searchParams.set(LANG_PARAM, lang);
    u.hash = '';
    return u.toString();
  } catch {
    return raveUrl;
  }
}
